import { useState, useEffect, useCallback } from 'react';
import { crmService } from '../../services/crmService';
import ContactModal from './ContactModal';
import ContactDetail from './ContactDetail';
import ContactImport from './ContactImport';
import OrganizationVerificationQueue from './OrganizationVerificationQueue';
import { useIsHQ, useIdentity } from '../../hooks/useIsHQ';
import { getInitialsAvatar } from '../../utils/imageCompress';

const POSITION_COLORS = {
  commander: '#DC2626', decision_maker: '#EA580C', process_owner: '#D97706',
  elected_official: '#7C3AED', political_appointee: '#6D28D9', congressional_staff: '#9333EA',
  end_user: '#16A34A', staff: '#2563EB', contractor_support: '#6B7280',
  researcher: '#0891B2', advisor: '#0D9488', liaison: '#0D9488',
};

const STAGE_COLORS = {
  unknown: '#374151', identified: '#1D4ED8', contacted: '#0369A1',
  engaged: '#0891B2', trusted: '#10B981', champion: '#22D3EE', lost: '#6B7280',
};

const FUNCTION_AREAS = [
  'S&T','R&D','Doctrine','Requirements','Policy','Safety',
  'Regulatory','Acquisition','Operations','Logistics','Finance','Legal','Medical','Communications',
];

export default function ContactList({ onSelectContact }) {
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [positionFilter, setPositionFilter] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [minAuthority, setMinAuthority] = useState('');
  const [page, setPage] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [orgFilter, setOrgFilter] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [canImport, setCanImport] = useState(false);
  const [showVerificationQueue, setShowVerificationQueue] = useState(false);
  const isHQ = useIsHQ();
  const me = useIdentity();
  const PAGE_SIZE = 50;

  // Bulk import: Headquarters, or HQ-designated subsidiary element leads
  useEffect(() => {
    if (isHQ) { setCanImport(true); return; }
    crmService.getTeam().then(team => {
      const mine = team.find(m =>
        (m.user_id && m.user_id === me.id) ||
        (m.email && me.email && m.email.toLowerCase() === me.email.toLowerCase())
      );
      setCanImport(!!mine?.is_subsidiary_lead);
    }).catch(() => setCanImport(false));
  }, [isHQ, me.id, me.email]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await crmService.getContacts({
        search: search || undefined,
        position_type: positionFilter || undefined,
        org_id: orgFilter || undefined,
        stage: stageFilter || undefined,
        min_authority: minAuthority || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setContacts(data.contacts || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, positionFilter, orgFilter, stageFilter, minAuthority, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    crmService.getOrganizations({ limit: 200 }).then(orgs => setOrganizations(orgs)).catch(() => {});
  }, []);

  const displayName = (c) => {
    const prefix = c.rank_code || c.grade_code || '';
    return `${prefix ? prefix + ' ' : ''}${c.first_name} ${c.last_name}`;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Filters */}
      <div className="controls-bar" style={{
        display: 'flex', flexWrap: 'wrap', gap: 'clamp(6px, 2vw, 8px)', padding: 'clamp(8px, 2vw, 12px) clamp(12px, 3vw, 16px)',
        backgroundColor: '#111827', borderBottom: '1px solid #1E2D45', alignItems: 'center',
      }}>
        <input
          type="text" placeholder="Search name, title, email…" value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          style={{ background: '#111827', border: '1px solid #1E2D45', color: '#F1F5F9', padding: 'clamp(6px, 1.5vw, 10px)', borderRadius: '4px', fontSize: 'clamp(11px, 2.8vw, 12px)', flex: '1 1 180px', minWidth: '140px' }}
        />
        <select value={positionFilter} onChange={e => { setPositionFilter(e.target.value); setPage(0); }}
          style={{ background: '#111827', border: '1px solid #1E2D45', color: '#F1F5F9', padding: 'clamp(6px, 1.5vw, 8px)', borderRadius: '4px', fontSize: 'clamp(11px, 2.8vw, 12px)', flex: '1 1 140px', minWidth: '110px' }}>
          <option value="">All Positions</option>
          {Object.keys(POSITION_COLORS).map(p => <option key={p} value={p}>{p.replace(/_/g,' ')}</option>)}
        </select>
        <select value={orgFilter} onChange={e => { setOrgFilter(e.target.value); setPage(0); }}
          style={{ background: '#111827', border: '1px solid #1E2D45', color: '#F1F5F9', padding: 'clamp(6px, 1.5vw, 8px)', borderRadius: '4px', fontSize: 'clamp(11px, 2.8vw, 12px)', flex: '1 1 140px', minWidth: '110px' }}>
          <option value="">All Organizations</option>
          {organizations.map(o => <option key={o.id} value={o.id}>{o.acronym || o.name}</option>)}
        </select>
        <select value={stageFilter} onChange={e => { setStageFilter(e.target.value); setPage(0); }}
          style={{ background: '#111827', border: '1px solid #1E2D45', color: '#F1F5F9', padding: 'clamp(6px, 1.5vw, 8px)', borderRadius: '4px', fontSize: 'clamp(11px, 2.8vw, 12px)', flex: '1 1 120px', minWidth: '90px' }}>
          <option value="">All Stages</option>
          {Object.keys(STAGE_COLORS).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={minAuthority} onChange={e => { setMinAuthority(e.target.value); setPage(0); }}
          style={{ background: '#111827', border: '1px solid #1E2D45', color: '#F1F5F9', padding: 'clamp(6px, 1.5vw, 8px)', borderRadius: '4px', fontSize: 'clamp(11px, 2.8vw, 12px)', flex: '1 1 120px', minWidth: '90px' }}>
          <option value="">Any Authority</option>
          <option value="50">50+ (Mid)</option>
          <option value="70">70+ (Senior)</option>
          <option value="85">85+ (Executive)</option>
          <option value="95">95+ (Apex)</option>
        </select>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 'clamp(4px, 1vw, 8px)', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button
            onClick={() => setShowVerificationQueue(!showVerificationQueue)}
            title="Verify suggested organization links"
            style={{ padding: 'clamp(6px, 1.5vw, 10px) clamp(10px, 2vw, 14px)', background: '#0D1F3C', border: '1px solid #22D3EE44', color: '#22D3EE', borderRadius: '4px', fontSize: 'clamp(11px, 2.8vw, 12px)', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}
          >
            🗺 Uncharted Territory
          </button>
          {canImport && (
            <button
              onClick={() => setShowImport(true)}
              title="Bulk import from CSV or Excel (HQ and subsidiary element leads)"
              style={{ padding: 'clamp(6px, 1.5vw, 10px) clamp(10px, 2vw, 14px)', background: '#0D1F3C', border: '1px solid #22D3EE44', color: '#22D3EE', borderRadius: '4px', fontSize: 'clamp(11px, 2.8vw, 12px)', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              ⬆ Import
            </button>
          )}
          <button
            onClick={() => { setEditContact(null); setShowModal(true); }}
            style={{ padding: 'clamp(6px, 1.5vw, 10px) clamp(10px, 2vw, 14px)', background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: '4px', fontSize: 'clamp(11px, 2.8vw, 12px)', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' }}
          >
            + Add Contact
          </button>
        </div>
      </div>

      {/* Stats bar */}
      <div style={{ padding: '6px 16px', backgroundColor: '#0A0F1C', borderBottom: '1px solid #111827' }}>
        <span style={{ color: '#6B7280', fontSize: '11px' }}>
          {total.toLocaleString()} contacts{positionFilter || search ? ' (filtered)' : ''}
        </span>
      </div>

      {/* Table / Card List */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <style>{`
          @media (max-width: 768px) {
            .contact-table { display: none; }
            .contact-cards { display: block; }
          }
          @media (min-width: 769px) {
            .contact-table { display: table; }
            .contact-cards { display: none; }
          }
        `}</style>

        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#94A3B8' }}>Loading…</div>
        ) : contacts.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
            No contacts found. Add your first contact to begin building the network.
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <table className="contact-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ backgroundColor: '#111827', position: 'sticky', top: 0 }}>
                  {['Name','Title / Position','Organization','Authority','Stage','Function Areas','Last Contact',''].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: '#6B7280', fontWeight: '500', borderBottom: '1px solid #1E2D45', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {contacts.map(c => (
                  <tr
                    key={c.id}
                    onClick={() => {
                      if (onSelectContact) { onSelectContact(c); return; }
                      setSelectedContact(c);
                    }}
                    style={{ borderBottom: '1px solid #111827', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#111827'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
                  >
                    <td style={{ padding: '8px 12px' }}>
                      <div style={{ color: '#F1F5F9', fontWeight: '500' }}>{displayName(c)}</div>
                      {c.service_branch && <div style={{ color: '#6B7280', fontSize: '10px' }}>{c.service_branch}</div>}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <div style={{ color: '#94A3B8' }}>{c.title || '—'}</div>
                      <span style={{
                        background: (POSITION_COLORS[c.position_type] || '#374151') + '22',
                        color: POSITION_COLORS[c.position_type] || '#6B7280',
                        padding: '1px 6px', borderRadius: '3px', fontSize: '10px',
                      }}>
                        {c.position_type?.replace(/_/g,' ')}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <div style={{ color: '#94A3B8' }}>{c.org_acronym || c.org_name || '—'}</div>
                      {c.org_type && <div style={{ color: '#4B5563', fontSize: '10px' }}>{c.org_type.replace(/_/g,' ')}</div>}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                      {c.authority_score ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                          <div style={{ width: '32px', height: '6px', borderRadius: '3px', backgroundColor: '#111827', overflow: 'hidden' }}>
                            <div style={{ width: `${c.authority_score}%`, height: '100%', backgroundColor: c.authority_score >= 75 ? '#22D3EE' : c.authority_score >= 50 ? '#3B82F6' : '#374151' }} />
                          </div>
                          <span style={{ color: c.authority_score >= 75 ? '#22D3EE' : '#94A3B8', fontWeight: '600' }}>
                            {c.authority_score}
                          </span>
                        </div>
                      ) : '—'}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{
                        background: (STAGE_COLORS[c.relationship_stage] || '#374151') + '33',
                        color: STAGE_COLORS[c.relationship_stage] || '#6B7280',
                        padding: '2px 8px', borderRadius: '10px', fontSize: '10px',
                      }}>
                        {c.relationship_stage}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap', maxWidth: '180px' }}>
                        {(c.function_areas || []).slice(0,3).map(fa => (
                          <span key={fa} style={{ background: '#111827', color: '#6B7280', padding: '1px 5px', borderRadius: '3px', fontSize: '9px' }}>{fa}</span>
                        ))}
                        {(c.function_areas || []).length > 3 && (
                          <span style={{ color: '#4B5563', fontSize: '9px' }}>+{c.function_areas.length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '8px 12px', color: '#6B7280' }}>
                      {c.last_interaction_date ? new Date(c.last_interaction_date).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ padding: '8px 6px', textAlign: 'right' }}>
                      <button
                        onClick={e => { e.stopPropagation(); setEditContact(c); setShowModal(true); }}
                        title="Edit contact"
                        style={{
                          background: 'transparent', color: '#4B5563',
                          border: '1px solid transparent', borderRadius: 4,
                          padding: '3px 7px', cursor: 'pointer', fontSize: 12,
                        }}
                        onMouseEnter={e => { e.currentTarget.style.color = '#94A3B8'; e.currentTarget.style.borderColor = '#374151'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = '#4B5563'; e.currentTarget.style.borderColor = 'transparent'; }}
                      >
                        ✏
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Card View */}
            <div className="contact-cards" style={{ display: 'none', padding: 'clamp(6px, 2vw, 12px)', gap: 'clamp(6px, 2vw, 8px)' }}>
              {contacts.map(c => (
                <div
                  key={c.id}
                  onClick={() => {
                    if (onSelectContact) { onSelectContact(c); return; }
                    setSelectedContact(c);
                  }}
                  style={{
                    background: '#111827', border: '1px solid #1E2D45', borderRadius: '6px',
                    padding: 'clamp(10px, 2.5vw, 14px)', marginBottom: 'clamp(6px, 2vw, 8px)', cursor: 'pointer', transition: 'background 0.2s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1E2D45'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = '#111827'}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 'clamp(6px, 1.5vw, 8px)', gap: '8px' }}>
                    <img
                      src={c.photo_url || getInitialsAvatar(c.first_name, c.last_name)}
                      alt={displayName(c)}
                      style={{
                        width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0,
                        border: '1px solid #22D3EE55', backgroundColor: '#111827',
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: '#F1F5F9', fontWeight: '600', fontSize: 'clamp(13px, 3.5vw, 14px)', wordBreak: 'break-word' }}>{displayName(c)}</div>
                      {c.service_branch && <div style={{ color: '#6B7280', fontSize: 'clamp(10px, 2.5vw, 11px)' }}>{c.service_branch}</div>}
                    </div>
                    <button
                      onClick={e => { e.stopPropagation(); setEditContact(c); setShowModal(true); }}
                      style={{
                        background: 'transparent', color: '#4B5563', border: 'none',
                        cursor: 'pointer', fontSize: '16px', padding: '4px 8px',
                      }}
                    >
                      ✏
                    </button>
                  </div>

                  <div style={{ fontSize: 'clamp(11px, 2.8vw, 12px)', color: '#94A3B8', marginBottom: 'clamp(6px, 1.5vw, 8px)', lineHeight: 1.4 }}>
                    {c.title || 'No title'} • {c.org_acronym || c.org_name || 'No org'}
                  </div>

                  <div style={{ display: 'flex', gap: 'clamp(6px, 2vw, 8px)', flexWrap: 'wrap', marginBottom: 'clamp(6px, 1.5vw, 8px)' }}>
                    <span style={{
                      background: (POSITION_COLORS[c.position_type] || '#374151') + '33',
                      color: POSITION_COLORS[c.position_type] || '#6B7280',
                      padding: 'clamp(2px, 0.5vw, 4px) clamp(5px, 1vw, 8px)', borderRadius: '3px', fontSize: 'clamp(10px, 2.5vw, 11px)', whiteSpace: 'nowrap',
                    }}>
                      {c.position_type?.replace(/_/g,' ')}
                    </span>
                    <span style={{
                      background: (STAGE_COLORS[c.relationship_stage] || '#374151') + '33',
                      color: STAGE_COLORS[c.relationship_stage] || '#6B7280',
                      padding: 'clamp(2px, 0.5vw, 4px) clamp(5px, 1vw, 8px)', borderRadius: '10px', fontSize: 'clamp(10px, 2.5vw, 11px)', whiteSpace: 'nowrap',
                    }}>
                      {c.relationship_stage}
                    </span>
                  </div>

                  {c.authority_score && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', marginBottom: '6px' }}>
                      <span style={{ color: '#6B7280', minWidth: '50px' }}>Authority</span>
                      <div style={{ flex: 1, height: '4px', borderRadius: '2px', backgroundColor: '#1E2D45', overflow: 'hidden' }}>
                        <div style={{ width: `${c.authority_score}%`, height: '100%', backgroundColor: c.authority_score >= 75 ? '#22D3EE' : c.authority_score >= 50 ? '#3B82F6' : '#374151' }} />
                      </div>
                      <span style={{ color: c.authority_score >= 75 ? '#22D3EE' : '#94A3B8', fontWeight: '600', minWidth: '28px' }}>
                        {c.authority_score}
                      </span>
                    </div>
                  )}

                  {(c.function_areas || []).length > 0 && (
                    <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap', fontSize: '10px' }}>
                      {(c.function_areas || []).slice(0,4).map(fa => (
                        <span key={fa} style={{ background: '#1E2D45', color: '#6B7280', padding: '1px 5px', borderRadius: '3px' }}>{fa}</span>
                      ))}
                      {(c.function_areas || []).length > 4 && (
                        <span style={{ color: '#4B5563' }}>+{c.function_areas.length - 4}</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Verification Queue */}
      {showVerificationQueue && (
        <OrganizationVerificationQueue />
      )}

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div style={{ padding: 'clamp(8px, 2vw, 12px) clamp(12px, 3vw, 16px)', display: 'flex', gap: 'clamp(6px, 2vw, 8px)', justifyContent: 'center', borderTop: '1px solid #1E2D45', backgroundColor: '#111827', flexWrap: 'wrap' }}>
          <button onClick={() => setPage(p => Math.max(0, p-1))} disabled={page === 0}
            style={{ padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2vw, 12px)', background: '#111827', border: '1px solid #1E2D45', color: '#94A3B8', borderRadius: '4px', cursor: 'pointer', fontSize: 'clamp(11px, 2.8vw, 12px)' }}>
            Prev
          </button>
          <span style={{ color: '#6B7280', fontSize: 'clamp(11px, 2.8vw, 12px)', alignSelf: 'center', whiteSpace: 'nowrap' }}>
            {page * PAGE_SIZE + 1}–{Math.min((page+1) * PAGE_SIZE, total)} of {total}
          </span>
          <button onClick={() => setPage(p => p+1)} disabled={(page+1)*PAGE_SIZE >= total}
            style={{ padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2vw, 12px)', background: '#111827', border: '1px solid #1E2D45', color: '#94A3B8', borderRadius: '4px', cursor: 'pointer', fontSize: 'clamp(11px, 2.8vw, 12px)' }}>
            Next
          </button>
        </div>
      )}

      {showModal && (
        <ContactModal
          contact={editContact}
          organizations={organizations}
          functionAreas={FUNCTION_AREAS}
          onSuccess={load}
          onClose={() => { setShowModal(false); setEditContact(null); }}
        />
      )}

      {selectedContact && (
        <ContactDetail
          contact={selectedContact}
          onClose={() => setSelectedContact(null)}
          onEdit={(c) => { setSelectedContact(null); setEditContact(c); setShowModal(true); }}
        />
      )}

      {showImport && (
        <ContactImport
          onClose={() => setShowImport(false)}
          onImported={load}
        />
      )}
    </div>
  );
}
